/**
 * Utilitaires purs chips ↔ filtres pour la recherche Shop.
 * Déterministes, sans LLM — testables en Vitest.
 */

import type { ShopSearchChip, ShopSearchFilters } from './types';

/** Convertit une liste de chips en filtres structurés (op `in` / `lte` / `gte`). */
export function chipsToFilters(chips: ShopSearchChip[]): ShopSearchFilters {
    const filters: ShopSearchFilters = {};

    for (const chip of chips) {
        const field = String(chip.field || '').trim();
        if (!field) continue;

        const op = String(chip.op || 'in').trim() || 'in';
        const value = chip.value;

        if (op === 'lte' || op === 'gte' || op === 'eq') {
            const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
            if (!Number.isFinite(num)) continue;
            filters[field] = { ...(filters[field] || {}), [op]: num };
            continue;
        }

        // Défaut : op `in` (multi-valeurs).
        const current = filters[field] || {};
        const existing = Array.isArray(current.in) ? [...(current.in as Array<string | number>)] : [];
        if (!existing.includes(value)) {
            existing.push(value);
        }
        filters[field] = { ...current, in: existing };
    }

    return filters;
}

/** Retire le chip à l'index donné. */
export function removeChipAt(chips: ShopSearchChip[], index: number): ShopSearchChip[] {
    if (index < 0 || index >= chips.length) {
        return chips.slice();
    }
    return chips.filter((_, i) => i !== index);
}

/**
 * Bascule un filtre d'option (ex. color=black) dans les chips.
 * Si le couple field/value existe déjà → retire ; sinon ajoute.
 */
export function toggleOptionChip(
    chips: ShopSearchChip[],
    field: string,
    value: string,
    label: string,
): ShopSearchChip[] {
    const f = field.trim();
    const v = value.trim();
    if (!f || !v) return chips.slice();

    const idx = chips.findIndex(
        (c) => c.field === f && String(c.value) === v && (c.op === 'in' || !c.op),
    );
    if (idx >= 0) {
        return removeChipAt(chips, idx);
    }

    return [...chips, { field: f, op: 'in', value: v, label: label || `${f}=${v}` }];
}

/** Pose ou remplace un filtre de disponibilité (bucket public). */
export function setAvailabilityChip(
    chips: ShopSearchChip[],
    bucket: string | null,
    label?: string,
): ShopSearchChip[] {
    const without = chips.filter((c) => c.field !== 'availability');
    if (!bucket) {
        return without;
    }
    return [
        ...without,
        {
            field: 'availability',
            op: 'in',
            value: bucket,
            label: label || bucket,
        },
    ];
}

/** Pose ou remplace un plafond de prix (lte). */
export function setPriceLteChip(
    chips: ShopSearchChip[],
    amount: number | null,
    label?: string,
): ShopSearchChip[] {
    const without = chips.filter((c) => !(c.field === 'price' && c.op === 'lte'));
    if (amount === null || !Number.isFinite(amount) || amount <= 0) {
        return without;
    }
    return [
        ...without,
        {
            field: 'price',
            op: 'lte',
            value: amount,
            label: label || `≤ ${amount}`,
        },
    ];
}

/** True si la carte expose une variante exacte vendable côté action panier. */
export function hasExactVariant(card: {
    cart_action?: { shop_variant_id?: number } | null;
    matched_variant?: { variant_id?: number } | null;
}): boolean {
    const fromAction = Number(card.cart_action?.shop_variant_id || 0);
    const fromMatch = Number(card.matched_variant?.variant_id || 0);
    return fromAction > 0 || fromMatch > 0;
}

export function variantIdOf(card: {
    cart_action?: { shop_variant_id?: number } | null;
    matched_variant?: { variant_id?: number } | null;
}): number {
    const fromAction = Number(card.cart_action?.shop_variant_id || 0);
    if (fromAction > 0) return fromAction;
    return Number(card.matched_variant?.variant_id || 0);
}
